import styled from '@emotion/styled';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import type { BaseSkill } from '~/shared/data-contract/skill';

import type {
    GlobalSearch,
    GlobalSearchJobLevelResponse,
    GlobalSearchJobRoleResponse,
} from '../../../data-contract/global-search';
import type { DropdownData } from '../../global-search/DropDown';
import { Dropdown } from '../../global-search/DropDown';
import { GlobalSearchInputV2 } from '../../global-search/GlobalSearchInputV2';
import type { TabParam } from '../../global-search/use-global-search-table-params';
import { useGlobalSearchTableParams } from '../../global-search/use-global-search-table-params';
import { useGlobalSearchQuery } from '../../query-hooks/global-search/global-search';
import { useGlobalSearchJobLevels } from '../../query-hooks/global-search/job-levels';
import { useGlobalSearchJobRoles } from '../../query-hooks/global-search/job-roles';
import { useJobArchitectureOptionsQuery } from '../../query-hooks/job-architecture-options';
import { CustomThemeProvider } from '../../theming';

const dropdownSearchLimit = 4;

const mapSkillsToDropdown = (
    selectedSkillIds: ReadonlySet<string>,
    skills: GlobalSearch['skills'] | undefined,
): readonly DropdownData[] => {
    return (
        skills?.data
            .filter((skill) => !selectedSkillIds.has(skill.id))
            .map((skill) => ({ id: skill.id, type: 'skill' as const })) ?? []
    );
};

const mapJobsToDropdown = (jobs: GlobalSearch['jobs'] | undefined): readonly DropdownData[] => {
    return jobs?.data.map((job) => ({ id: job.id, type: 'job' as const })) ?? [];
};

const mapUsersToDropdown = (users: GlobalSearch['users'] | undefined): readonly DropdownData[] => {
    return users?.data.map((user) => ({ id: user.id, type: 'user' as const })) ?? [];
};

const mapLearningOpportunitiesToDropdown = (
    learningOpportunities: GlobalSearch['learningOpportunities'] | undefined,
): readonly DropdownData[] => {
    return (
        learningOpportunities?.data.map((learningOpportunity) => ({
            id: learningOpportunity.id,
            type: 'learning-opportunity' as const,
        })) ?? []
    );
};

const mapJobRolesToDropdown = (jobRoles: GlobalSearchJobRoleResponse | undefined): readonly DropdownData[] => {
    return (
        jobRoles?.data.map((role) => ({
            clusterId: role.clusterId,
            familyId: role.familyId,
            id: role.id,
            jobArchitectureId: role.jobArchitectureId,
            type: 'job-role' as const,
        })) ?? []
    );
};

const mapJobLevelsToDropdown = (jobLevels: GlobalSearchJobLevelResponse | undefined): readonly DropdownData[] => {
    return (
        jobLevels?.data.map((level) => ({
            clusterId: level.clusterId,
            familyId: level.familyId,
            id: level.id,
            jobArchitectureId: level.jobArchitectureId,
            roleId: level.roleId,
            type: 'job-level' as const,
        })) ?? []
    );
};

const generateDropdownFlatList = (
    result: GlobalSearch | undefined,
    jobRolesResult: GlobalSearchJobRoleResponse | undefined,
    jobLevelsResult: GlobalSearchJobLevelResponse | undefined,
    selectedSkillIds: ReadonlySet<string>,
): readonly DropdownData[] => {
    const jobs = result?.jobs;
    const learningOpportunities = result?.learningOpportunities;
    const skills = result?.skills;
    const users = result?.users;

    return [
        ...mapSkillsToDropdown(selectedSkillIds, skills),
        ...mapJobsToDropdown(jobs),
        ...mapUsersToDropdown(users),
        ...mapLearningOpportunitiesToDropdown(learningOpportunities),
        ...mapJobRolesToDropdown(jobRolesResult),
        ...mapJobLevelsToDropdown(jobLevelsResult),
    ];
};

const SearchContainer = styled.div<{ readonly hasSelectedSkills: boolean; readonly expanded: boolean }>(
    ({ expanded, hasSelectedSkills, theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.m,
        margin: `0 ${theme.spacing.l}`,
        maxWidth: hasSelectedSkills ? '880px' : 'none',
        minWidth: hasSelectedSkills ? '400px' : 'auto',
        paddingTop: `${theme.spacing.xxs}`,
        transition: 'width 240ms cubic-bezier(0.16, 1, 0.3, 1)',
        width: hasSelectedSkills ? 'fit-content' : expanded ? '400px' : '320px',
    }),
);

export const GlobalSearchBarV2: FunctionComponent = () => {
    const {
        redirectToSearchTerm,
        redirectToSkillIds,
        searchTermResult: searchTermInitialValue,
        skillIds: skillIdsInitialValue,
    } = useGlobalSearchTableParams();

    const dropdownPositionRef = useRef<HTMLDivElement>(null);

    const [open, setDropdownOpen] = useState<boolean>(false);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState(searchTermInitialValue);
    const [dropdownSearchTermDebounced] = useDebounceValue(dropdownSearchTerm, 200);

    const [skillIds, setSkillIds] = useState<ReadonlyArray<string>>(skillIdsInitialValue);
    const [selectedSkills, setSelectedSkills] = useState<readonly BaseSkill[]>([]);
    const [currentCursor, setCurrentCursor] = useState<number>(-1);

    const { data: result, isFetching } = useGlobalSearchQuery(dropdownSearchTerm, skillIds);
    const selectedSkillIdSet = useMemo(() => new Set(skillIds), [skillIds]);
    const jobArchitectureOptions = useJobArchitectureOptionsQuery();
    const canSearchJobArchitecture =
        jobArchitectureOptions.isSuccess && jobArchitectureOptions.data.isOk
            ? jobArchitectureOptions.data.value.canUserGetJobArchitecture
            : false;
    const { data: jobRolesResult, isFetching: isJobRolesFetching } = useGlobalSearchJobRoles({
        enabled: canSearchJobArchitecture,
        searchTerm: dropdownSearchTermDebounced,
        size: dropdownSearchLimit,
    });
    const { data: jobLevelsResult, isFetching: isJobLevelsFetching } = useGlobalSearchJobLevels({
        enabled: canSearchJobArchitecture,
        searchTerm: dropdownSearchTermDebounced,
        size: dropdownSearchLimit,
    });

    const dropDownData = useMemo(
        () => generateDropdownFlatList(result, jobRolesResult, jobLevelsResult, selectedSkillIdSet),
        [jobLevelsResult, jobRolesResult, result, selectedSkillIdSet],
    );

    useEffect(() => {
        const updatedCursorIndex = is.nonEmptyArray(dropDownData) ? 0 : -1;
        setCurrentCursor(updatedCursorIndex);
    }, [dropDownData]);

    const onCloseDropdown = (): void => setDropdownOpen(false);
    const onOpenDropdown = (): void => setDropdownOpen(true);

    const getActiveDropdownElement = (): DropdownData | undefined => {
        if (currentCursor <= dropDownData.length - 1) {
            const active = dropDownData[currentCursor];
            if (!is.undefined(active)) {
                return active;
            }
        }

        return;
    };

    const handleGlobalSearch = (sectionTab?: TabParam): void => {
        if (is.nonEmptyArray(skillIds)) {
            redirectToSkillIds(skillIds, sectionTab);
        } else {
            redirectToSearchTerm(dropdownSearchTerm, sectionTab);
        }
        setDropdownSearchTerm('');
        onCloseDropdown();
    };

    const addSkillToSearch = (skillId: string): void => {
        const selectedSkill = result?.skills.data.find((skill) => skill.id === skillId);

        if (selectedSkill !== undefined) {
            setSelectedSkills((currentSkills) =>
                currentSkills.some((skill) => skill.id === skillId) ? currentSkills : [...currentSkills, selectedSkill],
            );
        }

        setSkillIds((currentSkillIds) =>
            currentSkillIds.includes(skillId) ? currentSkillIds : [...currentSkillIds, skillId],
        );
    };

    return (
        <ClickAwayListener onClickAway={onCloseDropdown}>
            <SearchContainer
                ref={dropdownPositionRef}
                expanded={open || is.nonEmptyArray(skillIds)}
                hasSelectedSkills={is.nonEmptyArray(skillIds)}
            >
                <GlobalSearchInputV2
                    addSkillToSearch={addSkillToSearch}
                    currentCursor={currentCursor}
                    dropDownData={dropDownData}
                    getActiveDropdownElement={getActiveDropdownElement}
                    handleGlobalSearch={handleGlobalSearch}
                    searchTerm={dropdownSearchTerm}
                    selectedSkills={selectedSkills}
                    setCurrentCursor={setCurrentCursor}
                    setSearchTerm={setDropdownSearchTerm}
                    setSkillIds={setSkillIds}
                    skillIds={skillIds}
                    onCloseDropdown={onCloseDropdown}
                    onOpenDropdown={onOpenDropdown}
                />
                <CustomThemeProvider>
                    <Dropdown
                        activeId={getActiveDropdownElement()?.id ?? ''}
                        addSkillToSearch={addSkillToSearch}
                        handleGlobalSearch={handleGlobalSearch}
                        isFetching={isFetching || isJobRolesFetching || isJobLevelsFetching}
                        jobLevelsResult={jobLevelsResult}
                        jobRolesResult={jobRolesResult}
                        open={open}
                        positionRef={dropdownPositionRef}
                        result={result}
                        searchTerm={dropdownSearchTerm}
                        selectedSkillIds={selectedSkillIdSet}
                        setSearchTerm={setDropdownSearchTerm}
                        onCloseDropdown={onCloseDropdown}
                    />
                </CustomThemeProvider>
            </SearchContainer>
        </ClickAwayListener>
    );
};
